import { useApolloClient } from '@apollo/client'
import React, { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { context, ContextProps, ModalState } from '@Domains/Club/Context'
import { ClubModal } from '@Domains/Club/Features/ClubModal/ClubModal'
import { useAuthenticateCartMutation, useMeQuery } from '@Generated/graphql'
import { useLocale } from '@Globals/Locale'
import { MetadataContext } from '@Globals/Metadata'

function statusToModalState(status: string | undefined): ModalState {
  switch (status) {
    case 'create-account':
      return 'CREATE_ACCOUNT'
    case 'success':
      return 'CLOSED'
    case 'unverified':
      return 'VERIFY_EMAIL'
    case 'sign-in':
      return 'SIGN_IN'
    default:
      return 'EMAIL_DECISION'
  }
}

export const ClubProvider: FC<PropsWithChildren> = ({ children }) => {
  const apolloClient = useApolloClient()
  const { storeURL } = useContext(MetadataContext)
  const locale = useLocale()
  const modalActionsRef = useNativeModalActions()

  const [defaultEmail, setDefaultEmail] = useState<string | undefined>(undefined)
  const [modalState, setModalState] = useState<ModalState>('CLOSED')
  const [queryParameters, setQueryParameters] = useState<{
    checksum?: string
    customerID?: string
    login: boolean
    recoveryToken?: string
    register: boolean
    resetPassword: boolean
  } | undefined>(undefined)
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [autenticateCart] = useAuthenticateCartMutation()

  const evictMeCache = useCallback(async () => {
    try {
      await apolloClient.refetchQueries({
        optimistic: true,
        updateCache(cache) {
          cache.evict({ fieldName: 'me' })
        },
      })
    } catch (err) {
      console.error('failed evicting me cache', err)
      apolloClient.cache.gc()
    }
  }, [apolloClient])

  // TODO: consider only calling the me endpoint when logging in
  const meQuery = useMeQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
      withAuth: true,
    },
    fetchPolicy: 'cache-first',
  })

  const check = useCallback<ContextProps['check']>(async ({ checksum, customerID, email }) => {
    setPasswordResetSuccess(false)
    if (!customerID && !email) {
      return null
    }

    const searchParams = new URLSearchParams()
    if (checksum) {
      searchParams.set('checksum', checksum)
    }
    if (customerID) {
      searchParams.set('customer_id', customerID)
    }
    if (email) {
      searchParams.set('email', email)
    }

    const res = await fetch(
      storeURL('/v1/auth/customers/check', searchParams, true),
      {
        method: 'POST',
      },
    )

    const data = await res.json() as { email?: string, status: string }

    return {
      nextModalState: statusToModalState(data?.status),
      email: email || data?.email,
      status: data?.status,
    }
  }, [storeURL])

  const [initialCheck, setInitialCheck] = useState<Awaited<ReturnType<typeof check>> | false>(false)

  useEffect(() => {
    if (initialCheck !== false) {
      return
    }

    if (!queryParameters) {
      return
    }

    if (!queryParameters.customerID) {
      setInitialCheck(null)
      return
    }

    (async () => {
      const result = await check({
        checksum: queryParameters.checksum,
        customerID: queryParameters.customerID,
      })
      setDefaultEmail(result?.email)
      setInitialCheck(result)
    })()
  }, [
    check,
    initialCheck,
    queryParameters,
  ])

  useEffect(() => {
    if (initialCheck === false) {
      return
    }

    if (!queryParameters) {
      return
    }

    if (queryParameters.login) {
      setModalState((prev) => {
        if (prev !== 'CLOSED') {
          return prev
        }

        return initialCheck?.nextModalState || 'EMAIL_DECISION'
      })
      setIsReady(true)
      return
    }

    if (queryParameters.register) {
      setModalState((prev) => {
        if (prev !== 'CLOSED') {
          return prev
        }

        return 'CREATE_ACCOUNT'
      })
      setIsReady(true)
      return
    }

    if (queryParameters.resetPassword && queryParameters.recoveryToken) {
      setModalState((prev) => {
        if (prev !== 'CLOSED') {
          return prev
        }

        return 'RESET_PASSWORD'
      })
      setIsReady(true)
      return
    }

    setIsReady(true)
  }, [queryParameters, initialCheck])

  const forgotPassword = useCallback<ContextProps['forgotPassword']>(async (email) => {
    setPasswordResetSuccess(false)
    const body = new URLSearchParams({
      email,
      locale,
    })

    if (queryParameters?.checksum) {
      body.set('checksum', queryParameters.checksum)
    }

    try {
      const res = await fetch(
        storeURL('/v1/auth/customers/forgot-password', undefined, true),
        {
          body: body.toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
          },
          method: 'POST',
        },
      )
      const data = await res.json() as { status: string }

      if (data?.status === 'error') {
        return 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'
      }

      if (data?.status === 'create-account') {
        return 'common:TRENDHIMCLUB_EMAIL_ACCOUNT_ERROR'
      }

      return null
    } catch (err) {
      console.error('failed resetting password', err)
      return 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'
    }
  }, [locale, queryParameters, storeURL])

  const login = useCallback(async (username: string, password: string): Promise<string | null> => {
    setPasswordResetSuccess(false)
    try {
      const res = await fetch(
        storeURL('/v1/auth/customers/login', undefined, true),
        {
          body: new URLSearchParams({ username, password }).toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
          },
          method: 'POST',
        },
      )
      const data = await res.json() as { status: string }
      ampli.caLoginRes({
        is_success: data?.status === 'success',
      })
      if (data?.status === 'success') {
        await evictMeCache()
        await autenticateCart({
          context: {
            domainProxy: true,
            userSpecific: true,
            withAuth: true,
          },
        })

        return null
      }

      return 'common:TRENDHIMCLUB_EMAIL_PASSWORD_ERROR'
    } catch (err) {
      console.error('failed logging in', err)
      return 'common:TRENDHIMCLUB_EMAIL_PASSWORD_ERROR'
    }
  }, [autenticateCart, evictMeCache, storeURL])

  const logout = useCallback(async (): Promise<string | null> => {
    setPasswordResetSuccess(false)
    try {
      const res = await fetch(
        storeURL('/v1/auth/customers/logout', undefined, true),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
          },
          method: 'POST',
        },
      )
      const data = await res.json() as { status: string }
      ampli.caLogoutRes({
        is_success: data?.status === 'success',
      })
      if (data?.status === 'success' || data?.status === 'error') {
        try {
          await evictMeCache()
          await autenticateCart({
            context: {
              domainProxy: true,
              userSpecific: true,
              withAuth: false,
            },
          })
        } catch (err) {
          console.error('failed evicting me cache', err)
        }
        return null
      }

      return 'TODO: unhandled logout status'
    } catch (err) {
      console.error('failed logging out', err)
      return 'TODO: unknown logout error'
    }
  }, [autenticateCart, evictMeCache, storeURL])

  const register = useCallback<ContextProps['register']>(async (email, password, firstName, phonePrefix, phone) => {
    setPasswordResetSuccess(false)
    const body = new URLSearchParams({
      first_name: firstName,
      locale,
      password,
      phone,
      phone_prefix: phonePrefix,
      username: email,
    })

    if (queryParameters?.checksum) {
      body.set('checksum', queryParameters.checksum)
    }

    try {
      const res = await fetch(
        storeURL('/v1/auth/customers/register', undefined, true),
        {
          body: body.toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
          },
          method: 'POST',
        },
      )
      const data = await res.json() as { status: string }

      ampli.caAccCreateRes({
        is_success: data?.status === 'success',
      })
      if (data?.status === 'error') {
        return {
          error: 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US',
          status: data?.status,
        }
      }

      setDefaultEmail(email)

      return {
        nextModalState: data?.status === 'success' ? 'SIGN_IN' : statusToModalState(data?.status),
        status: data?.status,
      }
    } catch (err) {
      console.error('failed registering', err)
      return {
        error: 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US',
      }
    }
  }, [locale, queryParameters, storeURL])

  const resetPassword = useCallback<ContextProps['resetPassword']>(async (password) => {
    setPasswordResetSuccess(false)
    const body = new URLSearchParams({
      locale,
      password,
    })

    if (queryParameters?.checksum) {
      body.set('checksum', queryParameters.checksum)
    }

    if (queryParameters?.customerID) {
      body.set('customer_id', queryParameters.customerID)
    }

    if (queryParameters?.recoveryToken) {
      body.set('recovery_token', queryParameters.recoveryToken)
    }

    try {
      const res = await fetch(
        storeURL('/v1/auth/customers/reset-password', undefined, true),
        {
          body: body.toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
          },
          method: 'POST',
        },
      )
      const data = await res.json() as { status: string }
      ampli.caPwdResetRes({
        is_success: data?.status === 'success',
      })
      if (data?.status === 'error') {
        return 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'
      }
      setPasswordResetSuccess(true)
      return null
    } catch (err) {
      console.error('failed resetting password', err)
      return 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'
    }
  }, [locale, queryParameters, storeURL])

  const verifyEmail = useCallback<ContextProps['verifyEmail']>(async (email) => {
    setPasswordResetSuccess(false)
    const body = new URLSearchParams({
      email,
      locale,
    })

    if (queryParameters?.checksum) {
      body.set('checksum', queryParameters.checksum)
    }

    try {
      const res = await fetch(
        storeURL('/v1/auth/customers/verify-email', undefined, true),
        {
          body: body.toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
          },
          method: 'POST',
        },
      )
      const data = await res.json() as { status: string }

      if (data?.status === 'error') {
        return 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'
      }

      if (data?.status === 'sign-in') {
        return 'common:TRENDHIMCLUB_EMAIL_ACCOUNT_ERROR'
      }

      return null
    } catch (err) {
      console.error('failed verifying email', err)
      return 'common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US'
    }
  }, [locale, queryParameters, storeURL])

  useEffect(() => {
    if (modalState === 'CLOSED') {
      modalActionsRef.current?.close()
      return
    }

    modalActionsRef.current?.open()
  }, [modalActionsRef, modalState])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    setQueryParameters((prev) => {
      if (prev) {
        return prev
      }

      const searchParams = new URLSearchParams(window.location.search)

      return {
        checksum: searchParams.get('thccs') || undefined,
        customerID: searchParams.get('thcid') || undefined,
        login: searchParams.has('login'),
        recoveryToken: searchParams.get('thcrt') || undefined,
        register: searchParams.has('register'),
        resetPassword: searchParams.has('reset_password'),
      }
    })
  }, [setQueryParameters])

  return (
    <context.Provider
      value={useMemo((): ContextProps => ({
        check,
        defaultEmail,
        forgotPassword,
        isLoggedIn: meQuery.data?.me?.__typename === 'Customer',
        isReady,
        login,
        logout,
        modalState,
        passwordResetSuccess,
        register,
        resetPassword,
        setDefaultEmail,
        setModalState,
        verifyEmail,
      }), [
        check,
        defaultEmail,
        forgotPassword,
        isReady,
        login,
        logout,
        meQuery.data?.me?.__typename,
        modalState,
        passwordResetSuccess,
        register,
        resetPassword,
        setDefaultEmail,
        setModalState,
        verifyEmail,
      ])}
    >
      <ClubModal modalActionsRef={modalActionsRef} />
      {children}
    </context.Provider>
  )
}

